import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGasI18n, useGasNotify, useGasConfirm } from '../../shared/kit';
import { formatPrice } from '../shop-shared/format';
import RestockAlertApp from '../shop-restock-alert/App';
import '../shop-restock-alert/style.css';
import { addToCart as apiAddToCart, emptyCart as apiEmptyCart, errorExtras } from './api';
import type {
    PhysicalAxis,
    PhysicalBuyboxData,
    PhysicalPublicVariant,
    ShopProductInitData,
} from './types';

interface PhysicalBuyboxProps {
    initData: ShopProductInitData;
    buybox: PhysicalBuyboxData;
    mountEl?: HTMLElement | null;
}

type Selection = Record<string, string>;

/**
 * Buy-box multi-axes (M6) : pills par définition, anti-aplat (inexistante ≠ épuisée),
 * swap prix/image, add_to_cart avec shop_variant_id, réassort M4 sur unavailable.
 */
export default function PhysicalBuybox({ initData, buybox, mountEl = null }: PhysicalBuyboxProps) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const { confirm } = useGasConfirm();

    const axes = buybox.axes;
    const variants = buybox.variants;

    const [selection, setSelection] = useState<Selection>(() => initialSelection(axes, variants));
    const [quantity, setQuantity] = useState(1);
    const [adding, setAdding] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

    const selectedVariant = useMemo(
        () => findVariantBySelection(variants, selection, axes),
        [variants, selection, axes],
    );

    const allAxesSelected = useMemo(
        () => axes.every((axis) => Boolean(selection[axis.key])),
        [axes, selection],
    );

    /** Combinaison complète mais absente de la matrice (anti-aplat). */
    const isMissingCombination = allAxesSelected && selectedVariant === null;

    const displayPrice = selectedVariant?.price ?? buybox.product_price;
    const displayImages = useMemo(() => {
        const fromVariant = selectedVariant?.images?.filter(Boolean) ?? [];
        if (fromVariant.length > 0) {
            return fromVariant;
        }
        if (selectedVariant?.image) {
            return [selectedVariant.image];
        }
        const fromProduct = buybox.product_images?.filter(Boolean) ?? [];
        if (fromProduct.length > 0) {
            return fromProduct;
        }
        return buybox.product_image ? [buybox.product_image] : [];
    }, [selectedVariant, buybox.product_image, buybox.product_images]);
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const displayImage = displayImages[Math.min(activeImageIdx, Math.max(0, displayImages.length - 1))] || '';

    // Swap galerie quand la sélection d'axe change.
    useEffect(() => {
        setActiveImageIdx(0);
    }, [selectedVariant?.id, selection]);

    const bucket = selectedVariant?.availability_bucket ?? null;
    const isUnavailable = bucket === 'unavailable';
    const isBackorder = bucket === 'backorder';
    const canPurchase =
        selectedVariant !== null
        && !isUnavailable
        && (bucket === 'in_stock' || bucket === 'low_stock' || bucket === 'backorder');

    const onSelectOption = useCallback((axisKey: string, slug: string) => {
        setSelection((prev) => ({ ...prev, [axisKey]: slug }));
        setMessage(null);
    }, []);

    const optionState = useCallback(
        (axis: PhysicalAxis, optionSlug: string): 'available' | 'unavailable' | 'missing' | 'selected' => {
            const next: Selection = { ...selection, [axis.key]: optionSlug };
            // Si d'autres axes ne sont pas encore choisis, juger sur existence partielle.
            const complete = axes.every((a) => Boolean(next[a.key]));
            if (!complete) {
                // Au moins une variante active matche les options déjà choisies + celle-ci.
                const partialMatch = variants.some((v) => matchesPartial(v, next));
                if (!partialMatch) {
                    return 'missing';
                }
                return selection[axis.key] === optionSlug ? 'selected' : 'available';
            }
            const match = findVariantBySelection(variants, next, axes);
            if (!match) {
                return 'missing';
            }
            if (match.availability_bucket === 'unavailable') {
                return selection[axis.key] === optionSlug ? 'selected' : 'unavailable';
            }
            return selection[axis.key] === optionSlug ? 'selected' : 'available';
        },
        [selection, axes, variants],
    );

    const doAdd = useCallback(async () => {
        if (!selectedVariant || !canPurchase) {
            return;
        }
        setAdding(true);
        setMessage(null);
        try {
            const data = await apiAddToCart(initData, {
                product_id: initData.product_id,
                shop_product_id: initData.shop_product_id ?? buybox.restock?.product_id ?? undefined,
                product_type: initData.product_type,
                variante_id: 'default',
                quantity,
                shop_variant_id: selectedVariant.id,
            });
            if (data.cart_was_cleared && data.info_message) {
                setMessage({ type: 'info', text: data.info_message });
            }
            setMessage({
                type: 'success',
                text: data.message || t('shop.cart.added_success'),
            });
            if (typeof data.cart_count === 'number') {
                document.querySelectorAll('.gas-shop-cart-count').forEach((node) => {
                    node.textContent = String(data.cart_count);
                });
            }
            document.dispatchEvent(new CustomEvent('gas_cart_updated', { detail: data }));
            const el = mountEl ?? document.getElementById('gas-shop-product-app');
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (error) {
            const extras = errorExtras(error);
            if (extras.require_login) {
                window.location.href = initData.login_url;
                return;
            }
            if (extras.require_cart_action) {
                const ok = await confirm({
                    title: t('shop.cart.action_required_title'),
                    message: t('shop.cart.action_required_empty_and_add'),
                    confirmLabel: t('shop.cart.empty_and_add'),
                    tone: 'warning',
                });
                if (ok) {
                    try {
                        await apiEmptyCart(initData);
                        await doAdd();
                    } catch (e) {
                        setMessage({ type: 'error', text: (e as Error).message || t('errors.generic') });
                    }
                }
                return;
            }
            notify.error((error as Error).message || t('errors.generic'));
            setMessage({ type: 'error', text: (error as Error).message || t('errors.generic') });
        } finally {
            setAdding(false);
        }
    }, [
        selectedVariant,
        canPurchase,
        initData,
        buybox.restock?.product_id,
        quantity,
        t,
        mountEl,
        confirm,
        notify,
    ]);

    const showBackorderBadge = buybox.show_backorder_badge !== false;

    const bucketLabel = (b: string | null): string => {
        if (!b) {
            return '';
        }
        if (b === 'unavailable') {
            return t('shop.product.variants.sold_out');
        }
        if (b === 'backorder') {
            return showBackorderBadge ? t('shop.product.variants.on_backorder') : '';
        }
        if (b === 'low_stock') {
            return t('shop.product.variants.low_stock');
        }
        if (b === 'in_stock') {
            return t('shop.product.variants.in_stock');
        }
        return t(`shop.product.availability.${b}`, {}, b);
    };

    const restockInit = useMemo(() => {
        if (!buybox.restock || !selectedVariant || !isUnavailable) {
            return null;
        }
        return {
            product_id: buybox.restock.product_id,
            variant_id: selectedVariant.id,
            is_logged_in: buybox.restock.is_logged_in,
            subscribed: false,
            status: null as string | null,
            locale: buybox.restock.locale,
            config: buybox.restock.config,
        };
    }, [buybox.restock, selectedVariant, isUnavailable]);

    return (
        <div
            className="gas-shop-physical-buybox"
            data-product-id={initData.product_id}
            data-shop-product-id={initData.shop_product_id ?? ''}
        >
            {displayImage ? (
                <div className="gas-shop-physical-buybox__image-wrap" data-gas-buybox-gallery="1">
                    <img
                        className="gas-shop-physical-buybox__image"
                        src={displayImage}
                        alt=""
                        data-gas-buybox-image="1"
                    />
                    {displayImages.length > 1 ? (
                        <div className="gas-shop-physical-buybox__thumbs" role="list">
                            {displayImages.map((src, idx) => (
                                <button
                                    key={`${src}-${idx}`}
                                    type="button"
                                    className={
                                        'gas-shop-physical-buybox__thumb'
                                        + (idx === activeImageIdx ? ' is-active' : '')
                                    }
                                    onClick={() => setActiveImageIdx(idx)}
                                    aria-label={t('shop.product.gallery.thumb', { n: String(idx + 1) })}
                                    data-gas-buybox-thumb={idx}
                                >
                                    <img src={src} alt="" />
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <p className="gas-shop-physical-buybox__intro">
                {t('shop.product.variants.choose_options')}
            </p>

            {axes.map((axis) => (
                <div
                    key={axis.key}
                    className="gas-shop-physical-buybox__axis"
                    data-axis-key={axis.key}
                    role="group"
                    aria-label={t('shop.product.variants.choose_axis', { label: axis.label })}
                >
                    <div className="gas-shop-physical-buybox__axis-label">{axis.label}</div>
                    <div className="gas-shop-physical-buybox__pills">
                        {axis.options.map((opt) => {
                            const state = optionState(axis, opt.slug);
                            const isSelected = selection[axis.key] === opt.slug;
                            const isMissing = state === 'missing';
                            const isSoldOut = state === 'unavailable';
                            const disabled = isMissing;
                            const title = isMissing
                                ? t('shop.product.variants.combination_missing')
                                : isSoldOut
                                    ? t('shop.product.variants.sold_out')
                                    : opt.label;
                            return (
                                <button
                                    key={opt.slug}
                                    type="button"
                                    className={[
                                        'gas-shop-physical-buybox__pill',
                                        isSelected ? 'gas-shop-physical-buybox__pill--selected' : '',
                                        isMissing ? 'gas-shop-physical-buybox__pill--missing' : '',
                                        isSoldOut ? 'gas-shop-physical-buybox__pill--sold-out' : '',
                                    ].filter(Boolean).join(' ')}
                                    disabled={disabled}
                                    aria-pressed={isSelected}
                                    aria-disabled={disabled || undefined}
                                    title={title}
                                    data-option-slug={opt.slug}
                                    data-option-state={state}
                                    onClick={() => {
                                        if (!disabled) {
                                            onSelectOption(axis.key, opt.slug);
                                        }
                                    }}
                                >
                                    <span>{opt.label}</span>
                                    {isSoldOut ? (
                                        <span className="gas-shop-physical-buybox__pill-hint">
                                            {t('shop.product.variants.sold_out')}
                                        </span>
                                    ) : null}
                                    {isMissing ? (
                                        <span className="gas-shop-physical-buybox__pill-hint">
                                            {t('shop.product.variants.combination_unavailable')}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            <div className="gas-shop-physical-buybox__price" data-gas-buybox-price="1">
                {displayPrice !== null && displayPrice !== undefined ? (
                    <strong>{formatPrice(displayPrice)}</strong>
                ) : null}
                {selectedVariant && bucket && bucketLabel(bucket) ? (
                    <span
                        className={`gas-shop-physical-buybox__bucket gas-shop-physical-buybox__bucket--${bucket}`}
                        data-availability-bucket={bucket}
                    >
                        {bucketLabel(bucket)}
                    </span>
                ) : null}
            </div>

            {isMissingCombination ? (
                <p className="gas-shop-physical-buybox__status gas-shop-physical-buybox__status--missing" role="status">
                    {t('shop.product.variants.combination_missing')}
                </p>
            ) : null}

            {isUnavailable && selectedVariant ? (
                <p className="gas-shop-physical-buybox__status gas-shop-physical-buybox__status--sold-out" role="status">
                    {t('shop.product.variants.sold_out')}
                </p>
            ) : null}

            {isBackorder && selectedVariant && showBackorderBadge ? (
                <p className="gas-shop-physical-buybox__status gas-shop-physical-buybox__status--backorder" role="status">
                    {t('shop.product.variants.on_backorder')}
                </p>
            ) : null}

            {message ? (
                <div className={`gas-shop-message gas-shop-${message.type}`}>
                    <span>{message.text}</span>
                    {message.type === 'success' ? (
                        <div className="gas-shop-cart-actions">
                            <a href={initData.cart_url} className="gas-shop-action-button">
                                {t('shop.cart.view_cart')}
                            </a>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {canPurchase ? (
                <form
                    className="gas-shop-add-to-cart-form gas-shop-physical-buybox__form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void doAdd();
                    }}
                >
                    <div className="gas-shop-form-actions">
                        <div className="gas-shop-quantity-selector">
                            <label htmlFor="gas-physical-qty">{t('shop.cart.quantity_label')}</label>
                            <select
                                id="gas-physical-qty"
                                value={quantity}
                                onChange={(e) => setQuantity(Number(e.target.value))}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="submit"
                            className="gas-shop-form-add-to-cart-button"
                            disabled={adding}
                            data-gas-buybox-add="1"
                            data-shop-variant-id={selectedVariant?.id}
                        >
                            {adding
                                ? t('shop.product.adding')
                                : t('shop.product.variants.add_to_cart')}
                        </button>
                    </div>
                </form>
            ) : !allAxesSelected ? (
                <p className="gas-shop-physical-buybox__hint" role="status">
                    {t('shop.product.variants.select_all_axes')}
                </p>
            ) : null}

            {restockInit ? (
                <div
                    className="gas-shop-restock-alert gas-shop-physical-buybox__restock"
                    data-gas-restock-mount="1"
                    data-variant-id={restockInit.variant_id}
                >
                    <RestockAlertApp key={restockInit.variant_id} initData={restockInit} />
                </div>
            ) : null}
        </div>
    );
}

function initialSelection(axes: PhysicalAxis[], variants: PhysicalPublicVariant[]): Selection {
    // Préfère la première variante vendable (in_stock / low_stock / backorder).
    const preferred =
        variants.find((v) => v.availability_bucket === 'in_stock' || v.availability_bucket === 'low_stock')
        ?? variants.find((v) => v.availability_bucket === 'backorder')
        ?? variants[0]
        ?? null;
    if (!preferred) {
        return {};
    }
    const sel: Selection = {};
    for (const axis of axes) {
        const slug = preferred.option_keys[axis.key];
        if (slug) {
            sel[axis.key] = slug;
        }
    }
    return sel;
}

function findVariantBySelection(
    variants: PhysicalPublicVariant[],
    selection: Selection,
    axes: PhysicalAxis[],
): PhysicalPublicVariant | null {
    if (axes.some((a) => !selection[a.key])) {
        return null;
    }
    return variants.find((v) => {
        for (const axis of axes) {
            if ((v.option_keys[axis.key] || '') !== selection[axis.key]) {
                return false;
            }
        }
        return true;
    }) ?? null;
}

function matchesPartial(variant: PhysicalPublicVariant, selection: Selection): boolean {
    for (const [key, slug] of Object.entries(selection)) {
        if (!slug) {
            continue;
        }
        if ((variant.option_keys[key] || '') !== slug) {
            return false;
        }
    }
    return true;
}
